/**
 * Keeps a short, consent-aware list of products viewed in this browser tab.
 * Only Shopify product IDs are stored; no customer or product details are persisted.
 */
export class RecentlyViewed {
  /** @static @constant {string} Versioned key used in session storage. */
  static #STORAGE_KEY = 'bouchilino:viewed-products:v1';
  /** @static @constant {string} Horizon's previous local-storage key. */
  static #LEGACY_STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 4;

  /**
   * @param {string | number} productId
   * @returns {string | null}
   */
  static #normalizeProductId(productId) {
    const normalizedId = String(productId).trim();
    return /^\d+$/.test(normalizedId) ? normalizedId : null;
  }

  /** @returns {boolean} */
  static #preferencesAllowed() {
    try {
      const customerPrivacy = globalThis.Shopify?.customerPrivacy;
      return Boolean(
        customerPrivacy &&
          typeof customerPrivacy.preferencesProcessingAllowed === 'function' &&
          customerPrivacy.preferencesProcessingAllowed()
      );
    } catch {
      return false;
    }
  }

  static #clearLegacyStorage() {
    try {
      globalThis.localStorage?.removeItem(this.#LEGACY_STORAGE_KEY);
    } catch {
      // Storage can be unavailable in private browsing; the feature then stays disabled.
    }
  }

  /**
   * Adds a product to the recently viewed products list.
   * @param {string | number} productId - The Shopify product ID to add.
   */
  static addProduct(productId) {
    const normalizedId = this.#normalizeProductId(productId);
    this.#clearLegacyStorage();

    if (!normalizedId) return;

    if (!this.#preferencesAllowed()) {
      this.clearProducts();
      return;
    }

    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== normalizedId);
    viewedProducts.unshift(normalizedId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    try {
      globalThis.sessionStorage?.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
    } catch (error) {
      console.warn('Unable to save recently viewed products:', error);
    }
  }

  static clearProducts() {
    this.#clearLegacyStorage();
    try {
      globalThis.sessionStorage?.removeItem(this.#STORAGE_KEY);
    } catch (error) {
      console.warn('Unable to clear recently viewed products:', error);
    }
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    this.#clearLegacyStorage();

    if (!this.#preferencesAllowed()) {
      this.clearProducts();
      return [];
    }

    try {
      const value = JSON.parse(globalThis.sessionStorage?.getItem(this.#STORAGE_KEY) || '[]');
      return Array.isArray(value)
        ? value
            .map((id) => this.#normalizeProductId(id))
            .filter((id) => id !== null)
            .slice(0, this.#MAX_PRODUCTS)
        : [];
    } catch (error) {
      console.warn('Unable to read recently viewed products:', error);
      try {
        globalThis.sessionStorage?.removeItem(this.#STORAGE_KEY);
      } catch {
        // Ignore storage cleanup failures and keep the storefront usable.
      }
      return [];
    }
  }
}
